/**
 * ================================================================================================
 * >>> **HER LIVE FILTERS — BUILT IN-HOUSE, ON HER DEVICE, NO VENDOR AND NO PER-MINUTE COST.** <<<
 *
 * Her ruling: *"the filtered feed IS the broadcast feed (audience sees the filtered her), choice
 * remembered per member, camera-flip safe."*
 *
 * >>> **AND HER AMENDMENT GOVERNS EVERYTHING BELOW: FILTERS ARE OPTIONAL AND OFF BY DEFAULT.** <<<
 * *"toggled OFF (the default), the camera broadcasts bare, no processing at all."* Not a preset
 * called Off with the pipeline still running — NO PROCESSING. A member who never wants a filter
 * never pays a frame of GPU for one, and never sees the carousel again unless she flips the switch.
 *
 * **THIS MODULE IMPORTS NOTHING**, which is the entry requirement that lets a FAST fixture RUN every
 * law in it rather than read one out of a component (R146).
 * ================================================================================================
 * >>> **HER MIRROR TEST REJECTED THE FIRST SET, AND THE NUMBERS BELOW ARE WHY IT WAS RIGHT.** <<<
 *
 * Her verdict, 2026-08-27: *"they read as colour-scheme changes, not face filters."* That was not
 * taste — it was arithmetic, and the old presets could not have produced anything else:
 *
 *   - The smoothing sampled a **five-pixel** neighbourhood (nine taps at +-2px). A blemish on a face
 *     filling a portrait phone frame is **fifteen to fifty pixels across**. A five-pixel average
 *     cannot touch one; it removes sensor noise. So the smoothing was invisible **by construction**.
 *   - `warm` ran as high as 0.52 and `brighten` as high as 0.22, full-frame, with big coefficients.
 *
 * So the only thing her eye COULD see was the colour move, which is exactly what she reported. The
 * repair is not a bigger number on the old shader — it is a different algorithm (frequency
 * separation, in `liveFilterPipeline.ts`), and these presets are re-pitched for it.
 * ================================================================================================
 */

/** OFF is the default and it is a real state, not a preset (her amendment). */
export const FILTERS_DEFAULT_ON = false;

/**
 * Her launch set, in her order, each a named composition of the knobs the shader carries.
 *
 * The knobs are declared here rather than in the shader so a fixture can read what a preset MEANS
 * without a GPU, and so her tuning is one number in one file rather than a rewrite of a program.
 *   `smooth`   0..1  blemish-level skin smoothing, edge-preserved by detail magnitude
 *   `brighten` 0..1  lift, weighted to the midtones so highlights do not blow
 *   `warm`     0..1  toward gold; this house's own direction, never toward magenta
 *   `bloom`    0..1  a soft light spill from the brightest parts
 *
 * >>> **THE SMOOTH NUMBERS ARE THE ONES THAT MOVED, AND THE COLOUR NUMBERS CAME DOWN.** <<< Her
 * complaint was two-sided — too little face, too much colour — so both sides are answered. `Natural`
 * and `Glow` are the two she judges, and they now lead with skin rather than with tint.
 */
export const LIVE_FILTERS = Object.freeze([
  { id: "natural", name: "Natural", smooth: 0.80, brighten: 0.10, warm: 0.04, bloom: 0.00 },
  { id: "glow", name: "Glow", smooth: 0.95, brighten: 0.16, warm: 0.10, bloom: 0.14 },
  { id: "golden", name: "Golden Hour", smooth: 0.75, brighten: 0.18, warm: 0.30, bloom: 0.12 },
  { id: "candlelight", name: "Candlelight", smooth: 0.80, brighten: 0.05, warm: 0.42, bloom: 0.22 },
]);

export const DEFAULT_FILTER_ID = "natural";

/**
 * >>> **HER INTENSITY SLIDER, AND WHY IT IS A MULTIPLIER RATHER THAN A FIFTH PRESET.** <<<
 *
 * Her ruling: *"adjustable intensity slider so each broadcaster tunes their own."* Skin is not one
 * thing — what reads as flattering on one face reads as plastic on another, and no set of four
 * presets can carry that. So the preset chooses the CHARACTER and this chooses HOW MUCH, which is
 * two facts rather than one (R18) and is why it is remembered on its own key.
 *
 * It scales the smoothing ONLY. Brighten, warm and bloom are the preset's identity — a member who
 * pulls Candlelight down to a third should still be in candlelight, not in a dimmer Natural.
 *
 * >>> **THE DEFAULT IS FULL, AND THE SLIDER ONLY EVER PULLS DOWN.** <<< A default below 1 means the
 * preset a member picks is not the preset she gets, and the number she is judging is a secret
 * product of two — which is precisely how the first set arrived at a strength nobody could see. The
 * preset states its own intent; her slider is how she takes some of it away.
 */
export const STRENGTH_DEFAULT = 1;
export const STRENGTH_MIN = 0.15;
export const STRENGTH_MAX = 1;

/**
 * A strength that is really a number in range, or the default.
 *
 * **PRESENCE, NEVER TRUTHINESS (R53/R136/R137):** this comes back off storage as a string, and
 * `Number("")` is 0 — which would silently pin a member's slider to nothing and read to her as a
 * filter that stopped working. An unreadable value is the DEFAULT, never zero.
 */
export function clampStrength(v) {
  const n = typeof v === "number" ? v : Number(String(v ?? "").trim());
  if (!Number.isFinite(n)) return STRENGTH_DEFAULT;
  return Math.min(STRENGTH_MAX, Math.max(STRENGTH_MIN, n));
}

/**
 * A preset by id, or null.
 *
 * **PRESENCE, NEVER TRUTHINESS (R53/R136/R137):** this comes back off storage, where everything is a
 * string and a stale id from last year is a real possibility. An id this house does not have is
 * NOTHING rather than the first preset — silently swapping her filter for a different one is the
 * kind of change nobody reports and everybody notices.
 */
export function filterById(id) {
  const key = typeof id === "string" ? id.trim() : "";
  return LIVE_FILTERS.find((f) => f.id === key) || null;
}

/**
 * What the camera should actually do, from the three remembered facts.
 *
 * >>> **THREE FACTS, NOT ONE (R18).** <<< *Is the switch on*, *which preset she likes* and *how hard
 * she wants it* are remembered SEPARATELY, so a member who flips filters off keeps her preset AND
 * her strength for the day she flips them back.
 *
 * The answer is the one thing the pipeline reads: `null` means DO NOT PROCESS AT ALL — no canvas, no
 * shader, no draw loop, the bare camera track on the wire.
 */
export function activeFilter({ on = FILTERS_DEFAULT_ON, id = DEFAULT_FILTER_ID, strength = STRENGTH_DEFAULT } = {}) {
  if (on !== true) return null;
  const preset = filterById(id) || filterById(DEFAULT_FILTER_ID);
  if (!preset) return null;
  // The slider is applied HERE rather than in the shader, so a fixture can read the number a preset
  // really broadcasts at without a GPU, and so the shader keeps one knob instead of two.
  return { ...preset, smooth: preset.smooth * clampStrength(strength) };
}

// ---------------------------------------------------------------------------------------------
// WHAT IS REMEMBERED, AND WHERE.
//
// `localStorage`, deliberately, and it is the opposite of `talkDoor.js`'s ruling one door over for a
// reason that is the mirror image of it: a landing INTENT must not outlive its session, and a
// member's own preference must. She sets Glow once and it is Glow next month.
//
// EVERY READ AND WRITE IS THROW-SAFE (R19): a storage that refuses is a browser setting, not a fact
// about her camera, and a throw on the go-live screen is a broadcast she never opened.
// ---------------------------------------------------------------------------------------------
const ON_KEY = "twelve.live.filters.on";
const ID_KEY = "twelve.live.filters.id";
const STRENGTH_KEY = "twelve.live.filters.strength";

export function readFilterPrefs(store) {
  let on = FILTERS_DEFAULT_ON;
  let id = DEFAULT_FILTER_ID;
  let strength = STRENGTH_DEFAULT;
  try {
    // PRESENCE: only the exact string this house writes is ON. A `"1"`, a `"yes"` or a leftover
    // value from some other build reads as OFF, which is her default and the cheap direction.
    on = store?.getItem?.(ON_KEY) === "on";
    const saved = store?.getItem?.(ID_KEY);
    if (filterById(saved)) id = String(saved).trim();
    const savedStrength = store?.getItem?.(STRENGTH_KEY);
    if (savedStrength !== null && savedStrength !== undefined) strength = clampStrength(savedStrength);
  } catch { /* a storage that refuses leaves her on the defaults */ }
  return { on, id, strength };
}

export function writeFilterPrefs(store, { on, id, strength } = {}) {
  try {
    if (typeof on === "boolean") store?.setItem?.(ON_KEY, on ? "on" : "off");
    if (filterById(id)) store?.setItem?.(ID_KEY, id);
    if (strength !== undefined) store?.setItem?.(STRENGTH_KEY, String(clampStrength(strength)));
    return true;
  } catch { return false; }
}
